/**
 * MomentumEngine - Pure friction-based physics for free scroll
 *
 * Unlike PhysicsEngine (damped spring with target), this just applies
 * friction decay with no attraction. Used for free scroll modes.
 *
 * TrackAnimator reads position/velocity directly to check for gear shifts.
 * No events needed - keeping it simple.
 */
export default class MomentumEngine {
	#position = 0;
	#velocity = 0;
	#friction;
	#prevTime = null;
	#isRunning = false;

	/**
	 * @param {Object} options
	 * @param {number} [options.friction=0.98] - Friction coefficient (0-1). Higher = more slippery.
	 * @throws {RangeError} If friction is not between 0 and 1 (exclusive)
	 */
	constructor({ friction = 0.98 } = {}) {
		if (friction <= 0 || friction >= 1) {
			throw new RangeError('Friction must be between 0 and 1 (exclusive)');
		}
		this.#friction = friction;
	}

	/**
	 * Start momentum animation from position with initial velocity
	 * @param {number} position - Starting position
	 * @param {number} velocity - Initial velocity
	 */
	start(position, velocity) {
		const _ = this;
		_.#position = position;
		_.#velocity = velocity;
		_.#prevTime = null;
		_.#isRunning = true;
	}

	/**
	 * Advance physics by one frame
	 * Called by TrackAnimator when momentum gear is engaged
	 * @param {number} time - Timestamp from frame engine
	 */
	tick(time) {
		if (!this.#isRunning) return;

		// Calculate time delta, default to ~60fps if first frame
		let timeDelta = this.#prevTime == null ? 16.67 : time - this.#prevTime;
		this.#prevTime = time;

		// Clamp time delta to prevent huge jumps after tab switches or pauses
		// Max 100ms (~10fps) to prevent position jumping too far
		// Min 0 to prevent negative deltas from clock adjustments
		timeDelta = Math.max(0, Math.min(timeDelta, 100));

		// Normalize to 60fps for consistent behavior across refresh rates
		const timeFactor = timeDelta / 16.67;

		// Apply friction (exponential decay)
		this.#velocity *= Math.pow(this.#friction, timeFactor);

		// Update position
		this.#position += this.#velocity * timeFactor;
	}

	/**
	 * Update position directly (used when track shifts for looping)
	 * @param {number} delta - Amount to add to position
	 */
	shiftPosition(delta) {
		this.#position += delta;
	}

	/**
	 * Stop immediately (used when shifting to physics gear or stopping)
	 */
	stop() {
		this.#velocity = 0;
		this.#isRunning = false;
	}

	/**
	 * Set friction coefficient
	 * @param {number} friction - Value between 0 and 1
	 */
	setFriction(friction) {
		this.#friction = friction;
	}

	// TrackAnimator reads these directly to check for gear shifts
	get position() {
		return this.#position;
	}

	get velocity() {
		return this.#velocity;
	}

	get isRunning() {
		return this.#isRunning;
	}
}
